import { StrictMode, useEffect } from "react";
import { Provider, useSelector } from "react-redux";
import { createStore, applyMiddleware } from "redux";
import { RootState, rootReducer, rootSaga } from "src/redux";
import MainRoutes from "./MainRoutes.js";
import { Auth0Provider } from "@auth0/auth0-react";
import { AUTH_CONFIG, REDIRECT } from "./common/Auth/auth0-variables.js";
import { createBrowserHistory } from "history";
import createSagaMiddleware from "redux-saga";
import { composeWithDevTools } from "redux-devtools-extension/developmentOnly";
import ReactGA from "react-ga4";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createCompassThemeObject } from "ctm-compass-react";
import {
  ChakraProvider,
  extendTheme,
  theme as defaultTheme,
} from "@chakra-ui/react";

import {
  AppInsightsContext,
  ReactPlugin,
} from "@microsoft/applicationinsights-react-js";
import { setupPendo } from "./services/pendo";
import {
  CHAKRA_COLOR_MODE,
  LIGHT_MODE_PREF,
  NO_COLOR_PREFERENCE,
} from "./common/constants.js";
import colors from "./common/colors.js";
import { TolgeeProvider } from "@tolgee/react";
import { tolgee } from "./tolgee";
import { getToken } from "./common";
import jwt_decode from "jwt-decode";

let colorPref = localStorage.getItem(CHAKRA_COLOR_MODE);

if (!colorPref && localStorage.getItem(LIGHT_MODE_PREF)) colorPref = "light";

if (!colorPref) sessionStorage.setItem(NO_COLOR_PREFERENCE, "true");

const components = {
  Popover: {
    variants: {},
    baseStyle: {
      body: {
        bg: "white",
      },
    },
    defaultProps: {},
  },
  FormLabel: {
    baseStyle: {
      color: colors.lightModeTextColor,
      fontSize: defaultTheme.fontSizes.sm,
      margin: 0,
      _dark: {
        color: "white",
      },
    },
  },
};

const config = {
  initialColorMode: colorPref || "dark",
  useSystemColorMode: false,
};

const themeObject: Record<string, any> = createCompassThemeObject({
  colors: {
    primary: "var(--primaryColor)",
    defaultText: "var(--defaultTextColor)",
    subtleText: "var(--subtleTextColor)",
  },
});

themeObject.components = { ...themeObject.components, ...components };
themeObject.config = config;

const theme = extendTheme(themeObject);

const history = createBrowserHistory();
const sagaMiddleWare = createSagaMiddleware();

// Initialize Google Analytics
ReactGA.initialize(import.meta.env.VITE_GA4_MEASUREMENT_ID);

const auth0Domain = AUTH_CONFIG.domain;
const auth0ClientId = AUTH_CONFIG.clientId;
const auth0CallbackUrl = AUTH_CONFIG.callbackUrl;
const auth0Audience = AUTH_CONFIG.audience;

function configureStore(preloadedState?: unknown) {
  const store = createStore(
    rootReducer, // root reducer with router state
    preloadedState,
    composeWithDevTools(
      applyMiddleware(
        sagaMiddleWare,
        // ... other middlewares ...
      ),
    ),
  );
  return store;
}

export const store = configureStore();

const queryClient = new QueryClient();

export type AppDispatch = typeof store.dispatch;

sagaMiddleWare.run(rootSaga);

const onRedirectCallback = (appState) => {
  if (appState?.returnTo) localStorage.setItem(REDIRECT, appState.returnTo);
};

export const reactPlugin = new ReactPlugin();

const appInsights = new ApplicationInsights({
  config: {
    instrumentationKey: import.meta.env.VITE_INSIGHTS_KEY,
    extensions: [reactPlugin],
    extensionConfig: {
      [reactPlugin.identifier]: { history },
    },
    autoTrackPageVisitTime: true,
    enableAutoRouteTracking: true,
  },
});
appInsights.loadAppInsights();

// Enrich all exceptions with userID from auth token
appInsights.addTelemetryInitializer((envelope) => {
  if (envelope.baseType === "ExceptionData") {
    envelope.baseData.properties = envelope.baseData.properties || {};
    try {
      const token = getToken()?.token;
      if (token) {
        const decoded = jwt_decode(token);
        envelope.baseData.properties.userID =
          decoded["https://www.ctmsmart.com/elysian/userid"];
        envelope.baseData.properties.debtor =
          decoded["https://www.ctmsmart.com/debtor"];
        envelope.baseData.properties.companyID =
          decoded["https://www.ctmsmart.com/defaultCompanyID"];
        const roles = decoded["https://www.ctmsmart.com/roles"];
        envelope.baseData.properties.roles = (
          Array.isArray(roles) ? roles : []
        )
          .map((r) => r?.code)
          .filter(Boolean)
          .join(",");
      }
    } catch (err) {
      // No token yet, user not logged in
    }
  }
  return true;
});

setupPendo();

const TolgeeSynchronizer = () => {
  const language = useSelector((state: RootState) => state.user.language);

  useEffect(() => {
    document.documentElement.lang = language;
    if (tolgee.getLanguage() !== language) {
      void tolgee.changeLanguage(language).catch((error) => {
        console.error(`Unable to load translations for ${language}`, error);
      });
    }
  }, [language]);

  return null;
};

const App = () => (
  <StrictMode>
    <TolgeeProvider tolgee={tolgee}>
      <ChakraProvider theme={theme}>
        <AppInsightsContext.Provider value={reactPlugin}>
          <QueryClientProvider client={queryClient}>
            <Provider store={store}>
              <TolgeeSynchronizer />
              <Auth0Provider
                domain={auth0Domain}
                clientId={auth0ClientId}
                cacheLocation="localstorage"
                onRedirectCallback={onRedirectCallback}
                authorizationParams={{
                  redirect_uri: auth0CallbackUrl,
                  audience: auth0Audience,
                  scope: "all:lightning",
                }}
              >
                <MainRoutes history={history} />
              </Auth0Provider>
            </Provider>
          </QueryClientProvider>
        </AppInsightsContext.Provider>
      </ChakraProvider>
    </TolgeeProvider>
  </StrictMode>
);

export default App;
